import { useTranslation } from "react-i18next";

import { LANG_STORAGE_KEY, SUPPORTED_LANGUAGES } from "@/i18n";
import type { SupportedLanguage } from "@/i18n";
import {
  useGetProfileQuery,
  useUpdateProfileMutation,
} from "@/queries/hooks/profileHooks";
import { notify } from "@/utils";

export function LanguageSettings() {
  const { t, i18n } = useTranslation("settings");
  const profileQuery = useGetProfileQuery();
  const updateProfile = useUpdateProfileMutation();

  const serverLang = profileQuery.data?.profile.preferred_language;
  const currentLang: SupportedLanguage =
    serverLang ?? (i18n.language.startsWith("es") ? "es" : "en");

  const handleSelect = async (lang: SupportedLanguage) => {
    if (lang === currentLang) return;
    try {
      await updateProfile.mutateAsync({ preferred_language: lang });
      await i18n.changeLanguage(lang);
      try {
        localStorage.setItem(LANG_STORAGE_KEY, lang);
      } catch {
        // ignore
      }
      notify.success(t("language.saved"));
    } catch (e) {
      notify.error(
        e instanceof Error ? e.message : t("language.saveError"),
      );
    }
  };

  const options: Array<{ value: SupportedLanguage; label: string }> = [
    { value: "en", label: t("language.english") },
    { value: "es", label: t("language.spanish") },
  ];

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-xl font-semibold text-garnet mb-2">
        {t("language.title")}
      </h3>
      <p className="text-sm text-garnet/80">{t("language.description")}</p>

      <div
        role="radiogroup"
        aria-label={t("language.title")}
        className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl"
      >
        {options.map((opt) => {
          const selected = currentLang === opt.value;
          return (
            <button
              type="button"
              key={opt.value}
              role="radio"
              aria-checked={selected}
              disabled={
                updateProfile.isPending && SUPPORTED_LANGUAGES.includes(opt.value)
              }
              onClick={() => handleSelect(opt.value)}
              className={`w-full text-left px-4 py-3 rounded-lg transition-colors font-semibold border-2 ${
                selected
                  ? "bg-gold/40 text-garnet border-gold"
                  : "hover:bg-accent/20 text-garnet/80 border-transparent bg-white"
              } ${
                updateProfile.isPending
                  ? "opacity-60 cursor-not-allowed"
                  : "cursor-pointer"
              }`}
            >
              <div className="text-base">{opt.label}</div>
              <div className="text-xs text-garnet/60 mt-1">
                {opt.value.toUpperCase()}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
